import axios from "axios";
import type { WarehouseDelivery, WarehouseDirection } from "../../interfaces/warehouse.interface";
import { attachAuthRefresh } from "../../apis/tokenRefresh";

const BASE_URL = process.env.REACT_APP_API_URL || "https://api-dev2.materialdepot.in/apiV1";

const client = axios.create({ baseURL: BASE_URL });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `JWT ${token}`;
  }
  return config;
});

attachAuthRefresh(client);

export interface WarehouseListParams {
  warehouse_id: number;
  direction: WarehouseDirection;
  include_previous?: boolean;
  search?: string;
  dispatch_date__gte?: string;
  dispatch_date__lte?: string;
  page?: number;
  page_size?: number;
}

export interface PaginatedWarehouseResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: WarehouseDelivery[];
}

export const getWarehouseDeliveries = async (params: WarehouseListParams): Promise<PaginatedWarehouseResponse | null> => {
  try {
    const query: Record<string, string | number> = {
      warehouse_id: params.warehouse_id,
      direction: params.direction,
      include_previous: params.include_previous ? "true" : "false",
      page: params.page ?? 1,
      page_size: params.page_size ?? 10,
    };
    if (params.search) query.search = params.search;
    if (params.dispatch_date__gte) query.dispatch_date__gte = params.dispatch_date__gte;
    if (params.dispatch_date__lte) query.dispatch_date__lte = params.dispatch_date__lte;

    const response = await client.get("/warehouse-manager/", { params: query });
    return response.data;
  } catch (e) {
    return null;
  }
};

export const receiveWarehouseDelivery = async (delivery_id: number, vw_id?: number) => {
  const body: Record<string, number> = { delivery_id };
  if (vw_id !== undefined) body.vw_id = vw_id;
  const response = await client.post("/warehouse-manager/receive/", body);
  return response.data;
};

export const dispatchWarehouseDelivery = async (
  delivery_id: number,
  vehicle_number?: string,
  driver_phone?: string,
) => {
  const body: Record<string, string | number> = { delivery_id };
  if (vehicle_number) body.vehicle_number = vehicle_number;
  if (driver_phone) body.driver_phone = driver_phone;
  const response = await client.post("/warehouse-manager/dispatch/", body);
  return response.data;
};

export const unifiedReceiveWarehouseDelivery = async (delivery_id: number, chosen_warehouse_id: number) => {
  const response = await client.post("/warehouse-manager/unified-receive/", { delivery_id, chosen_warehouse_id });
  return response.data;
};

export interface DownloadLegDocsResult {
  ok: boolean;
  errorCode?: string;
  message?: string;
}

const parseFilenameFromDisposition = (disposition: string | undefined, fallback: string): string => {
  if (!disposition) return fallback;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return match && match[1] ? decodeURIComponent(match[1]) : fallback;
};

const triggerBrowserDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const blobToText = (blob: Blob): Promise<string> => {
  if (typeof (blob as any).text === "function") {
    return (blob as any).text().then((t: string) => {
      if (t && t.length > 0) return t;
      return readBlobViaFileReader(blob);
    });
  }
  return readBlobViaFileReader(blob);
};

const readBlobViaFileReader = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });

export const downloadLegDocs = async (deliveryId: number): Promise<DownloadLegDocsResult> => {
  try {
    const response = await client.get(`/po-delivery-v2/${deliveryId}/download-docs/`, {
      responseType: "blob",
    });
    const headers = (response.headers || {}) as Record<string, string>;
    const disposition = headers["content-disposition"] || headers["Content-Disposition"];
    const filename = parseFilenameFromDisposition(disposition, `${deliveryId}_docs.pdf`);
    const data = response.data;
    const blob = data instanceof Blob ? data : new Blob([data as BlobPart]);
    triggerBrowserDownload(blob, filename);
    return { ok: true };
  } catch (e: any) {
    const resp = e?.response;
    if (resp && resp.data) {
      try {
        const text = resp.data instanceof Blob ? await blobToText(resp.data) : String(resp.data);
        const parsed = JSON.parse(text);
        return { ok: false, errorCode: parsed.error_code, message: parsed.message };
      } catch (_parseErr) {
        // fall through to generic error
      }
    }
    return { ok: false, errorCode: "UNKNOWN_ERROR", message: e?.message || "Download failed" };
  }
};
